import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, FormControl, IconButton, InputLabel, MenuItem, Paper, Popover, Select, Stack, Switch, Typography, useTheme } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SettingsIcon from '@mui/icons-material/Settings';
import { screenDesignSize } from '../../../../components/ScreenComposer/composer.types';
import { LiveViewQuality, LiveViewState, screensApi } from '../Screens.api';
import { Screen } from '../Screens.types';
import { connectWhep } from './whepClient';

export interface LiveViewProps {
    screen: Screen;
    onMessage: (message: string, severity?: 'success' | 'error') => void;
}

const STAGE_PADDING = 24;
const QUALITIES: LiveViewQuality[] = ['480p', '720p', '1080p'];
const BOS_LOGO = '/static/assets/beamOnScreen/BeamOnScreenDarkTheme.svg';

const logoGlow = keyframes`
    0%, 100% {
        filter:
            drop-shadow(0 0 6px rgba(254, 223, 153, 0.35))
            drop-shadow(0 0 16px rgba(254, 223, 153, 0.18));
        opacity: 0.78;
    }
    50% {
        filter:
            drop-shadow(0 0 10px rgba(254, 223, 153, 0.95))
            drop-shadow(0 0 28px rgba(254, 223, 153, 0.7))
            drop-shadow(0 0 48px rgba(254, 180, 60, 0.4));
        opacity: 1;
    }
`;

const remainLabel = (ms: number) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Live View tab: turn streaming on or off, pick a quality, and watch the screen over WHEP.
 * Streaming ends five minutes after it is turned on. Leaving the tab closes the viewer.
 */
export const LiveView: React.FC<LiveViewProps> = ({ screen, onMessage }) => {
    const theme = useTheme();
    const stageRef = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const tokenRef = useRef('');
    const onMessageRef = useRef(onMessage);
    const busyRef = useRef(false);
    onMessageRef.current = onMessage;
    const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
    const [view, setView] = useState<LiveViewState | null>(null);
    const [quality, setQuality] = useState<LiveViewQuality>('480p');
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const [localExpired, setLocalExpired] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const [qualityAnchor, setQualityAnchor] = useState<HTMLElement | null>(null);

    const design = useMemo(
        () => screenDesignSize(screen.deviceStatus?.resolution, screen.settings?.orientation),
        [screen.deviceStatus?.resolution, screen.settings?.orientation],
    );

    useEffect(() => {
        const el = stageRef.current;
        if (!el) return undefined;
        const update = () => setStageSize({ width: el.clientWidth, height: el.clientHeight });
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const scale = useMemo(() => {
        if (stageSize.width <= 0 || stageSize.height <= 0) return 0.5;
        return Math.min(1, (stageSize.width - STAGE_PADDING * 2) / design.width, (stageSize.height - STAGE_PADDING * 2) / design.height);
    }, [stageSize, design]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const next = await screensApi.liveView(screen._id);
                if (cancelled) return;
                tokenRef.current = next.viewer?.token || '';
                setView(next);
                if (next.enabled && next.quality && !busyRef.current) setQuality(next.quality);
                if (next.enabled && next.autoOffAt && new Date(next.autoOffAt).getTime() > Date.now()) setLocalExpired(false);
            } catch (error) {
                if (!cancelled) onMessageRef.current(error instanceof Error ? error.message : 'Could not read the live view', 'error');
            }
        };
        void load();
        const timer = window.setInterval(() => void load(), 5000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [screen._id]);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);

    const autoOffAt = view?.autoOffAt ? new Date(view.autoOffAt).getTime() : 0;
    const remaining = view?.enabled && autoOffAt ? autoOffAt - now : 0;
    useEffect(() => {
        if (view?.enabled && autoOffAt && remaining <= 0) setLocalExpired(true);
    }, [view?.enabled, autoOffAt, remaining]);

    const sessionKey = view?.enabled && !localExpired && view.viewer?.whepUrl ? `${view.enabledAt || ''}|${view.viewer.whepUrl}` : '';
    useEffect(() => {
        const video = videoRef.current;
        if (!sessionKey || !video) return undefined;
        const whepUrl = view?.viewer?.whepUrl || '';
        const controller = new AbortController();
        let connection: { close: () => void } | null = null;
        let stopped = false;
        const run = async () => {
            let attempt = 0;
            while (!stopped) {
                try {
                    const token = tokenRef.current;
                    if (!token) throw new Error('no viewer token');
                    const session = await connectWhep(whepUrl, token, video, controller.signal);
                    if (stopped) {
                        session.close();
                        return;
                    }
                    connection = session;
                    setPlaying(true);
                    attempt = 0;
                    await session.closed;
                } catch (error) {
                    if (stopped || (error instanceof DOMException && error.name === 'AbortError')) return;
                }
                setPlaying(false);
                connection = null;
                attempt += 1;
                const delay = Math.min(10_000, 1000 * 2 ** Math.min(attempt, 4));
                await new Promise((resolve) => window.setTimeout(resolve, delay));
            }
        };
        void run();
        return () => {
            stopped = true;
            controller.abort();
            connection?.close();
            setPlaying(false);
            if (video) video.srcObject = null;
        };
    }, [sessionKey, view?.viewer?.whepUrl]);

    const apply = async (change: { enabled?: boolean; quality?: LiveViewQuality }) => {
        setBusy(true);
        busyRef.current = true;
        try {
            const next = await screensApi.setLiveStream(screen._id, change);
            tokenRef.current = next.viewer?.token || '';
            setView(next);
            if (next.enabled && next.quality) setQuality(next.quality);
            setLocalExpired(Boolean(next.turnedOffAutomatically));
            onMessage(next.enabled ? `Live view is on at ${next.quality}` : 'Live view is off', 'success');
        } catch (error) {
            onMessage(error instanceof Error ? error.message : 'Could not change the live view', 'error');
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };

    useEffect(() => {
        const onChange = () => setFullScreen(document.fullscreenElement === stageRef.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullScreen = () => {
        const stage = stageRef.current;
        if (!stage) return;
        if (document.fullscreenElement === stage) void document.exitFullscreen();
        else void stage.requestFullscreen();
    };

    const expired = localExpired || Boolean(view?.turnedOffAutomatically);
    const waiting = Boolean(view?.enabled) && !expired && !playing;
    const showCountdown = Boolean(view?.enabled) && remaining > 0 && !expired;
    const cornerButton = {
        color: 'common.white',
        backgroundColor: alpha('#000', 0.45),
        '&:hover': { backgroundColor: alpha('#000', 0.65) },
    };
    const offMessage = expired
        ? 'Streaming was turned off automatically after 5 minutes'
        : 'Live streaming is off for this screen';

    return (
        <Stack spacing={2}>
            {view && !view.configured && (
                <Alert severity="warning">Live view streaming is not configured on this server.</Alert>
            )}
            {view?.device?.state === 'error' && view.device.lastError && (
                <Alert severity="error">{view.device.lastError}</Alert>
            )}
            <Paper
                ref={stageRef}
                variant="outlined"
                sx={{
                    height: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 240px)' },
                    minHeight: 420,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.04)',
                    backgroundImage:
                        theme.palette.mode === 'dark'
                            ? 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)'
                            : 'radial-gradient(rgba(0,0,0,0.10) 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                }}
            >
                <Box
                    sx={{
                        width: design.width * scale,
                        height: design.height * scale,
                        position: 'relative',
                        flex: '0 0 auto',
                        backgroundColor: '#000',
                        boxShadow: `0 8px 32px ${alpha('#000', theme.palette.mode === 'dark' ? 0.6 : 0.25)}`,
                        overflow: 'hidden',
                    }}
                >
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        aria-label={`${screen.name} live view`}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', display: playing && !expired ? 'block' : 'none' }}
                    />
                    {(!playing || expired) && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
                            {waiting && (
                                <Box
                                    component="img"
                                    src={BOS_LOGO}
                                    alt="Beam on Screen"
                                    sx={{
                                        width: 96,
                                        height: 96,
                                        objectFit: 'contain',
                                        animation: `${logoGlow} 2.4s ease-in-out infinite`,
                                        '@media (prefers-reduced-motion: reduce)': {
                                            animation: 'none',
                                            opacity: 1,
                                            filter: 'drop-shadow(0 0 16px rgba(254, 223, 153, 0.75))',
                                        },
                                    }}
                                />
                            )}
                            <Typography variant="body1" sx={{ color: 'common.white', textAlign: 'center' }}>
                                {waiting ? 'Waiting for the screen…' : offMessage}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 6,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        maxWidth: 'calc(100% - 56px)',
                        borderRadius: 5,
                        backgroundColor: alpha('#000', 0.45),
                        pl: 0.25,
                        pr: 1.25,
                        py: 0.25,
                    }}
                >
                    <Switch
                        color="success"
                        size="small"
                        checked={Boolean(view?.enabled) && !expired}
                        disabled={busy || !view?.configured}
                        onChange={(_, on) => void apply(on ? { enabled: true, quality } : { enabled: false })}
                        inputProps={{ 'aria-label': Boolean(view?.enabled) && !expired ? 'Turn off' : 'Turn on' }}
                    />
                    <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 600, lineHeight: 1.2 }}>
                        {Boolean(view?.enabled) && !expired ? 'Turn off' : 'Turn on'}
                    </Typography>
                    {showCountdown && (
                        <Typography variant="body2" sx={{ color: 'common.white', lineHeight: 1.2 }}>
                            Stops automatically in {remainLabel(remaining)}
                        </Typography>
                    )}
                </Box>
                <IconButton aria-label={fullScreen ? 'Exit full screen' : 'Full screen'} onClick={toggleFullScreen} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 6, ...cornerButton }}>
                    {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
                <IconButton aria-label="Stream quality" onClick={(event) => setQualityAnchor(event.currentTarget)} sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 6, ...cornerButton }}>
                    <SettingsIcon />
                </IconButton>
                <Popover
                    open={Boolean(qualityAnchor)}
                    anchorEl={qualityAnchor}
                    onClose={() => setQualityAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Box sx={{ p: 2, width: 200 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="live-view-quality">Quality</InputLabel>
                            <Select
                                labelId="live-view-quality"
                                label="Quality"
                                value={quality}
                                onChange={(event) => {
                                    const next = event.target.value as LiveViewQuality;
                                    setQuality(next);
                                    if (view?.enabled && !expired) void apply({ quality: next });
                                }}
                            >
                                {QUALITIES.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Popover>
            </Paper>
        </Stack>
    );
};
